import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { warehouseAPI } from '../../api/warehouses';
import { Warehouse, WarehouseInventory } from '../../types/entities';
import { ArrowLeft, Edit, Package, AlertTriangle, Download, ScrollText } from 'lucide-react';
import toast from 'react-hot-toast';
import { WarehouseInfoCard } from '../../components/warehouse/WarehouseInfoCard';
import { InventoryFilterPanel, InventoryFilters } from '../../components/warehouse/InventoryFilterPanel';
import { InventoryTable } from '../../components/warehouse/InventoryTable';
import { Pagination } from '../../components/UI/Pagination';
import { ItemHistoryModal } from '../../components/warehouse/ItemHistoryModal';
import { ReconciliationModal } from '../../components/warehouse/ReconciliationModal';
import SnapshotDatePicker from '../../components/warehouse/SnapshotDatePicker';
import SnapshotResultTable from '../../components/warehouse/SnapshotResultTable';

export const WarehouseDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [warehouse, setWarehouse] = useState<Warehouse | null>(null);
  const [inventory, setInventory] = useState<WarehouseInventory[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filter state from URL params
  const [filters, setFilters] = useState<InventoryFilters>({
    itemName: searchParams.get('itemName') || undefined,
    unit: searchParams.get('unit') || undefined,
    stockStatus: searchParams.get('stockStatus') || undefined,
  });
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('pageSize') || '20', 10));

  // History modal state
  const [historyModal, setHistoryModal] = useState({
    isOpen: false,
    itemId: 0,
    itemName: '',
  });

  // Reconciliation modal state
  const [reconcileModal, setReconcileModal] = useState({
    isOpen: false,
    itemId: 0,
    itemName: '',
    systemStock: 0,
    unit: '',
  });

  // Snapshot state
  const [snapshotDate, setSnapshotDate] = useState<string | null>(null);
  const [snapshotData, setSnapshotData] = useState<any>(null);
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);
  const [exportingSnapshot, setExportingSnapshot] = useState(false);

  useEffect(() => {
    if (id) {
      loadWarehouse(parseInt(id));
      loadInventory(parseInt(id));
    }
  }, [id, filters, page, pageSize]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.itemName) params.set('itemName', filters.itemName);
    if (filters.unit) params.set('unit', filters.unit);
    if (filters.stockStatus) params.set('stockStatus', filters.stockStatus);
    if (page > 1) params.set('page', page.toString());
    if (pageSize !== 20) params.set('pageSize', pageSize.toString());
    setSearchParams(params);
  }, [filters, page, pageSize]);

  const loadWarehouse = async (warehouseId: number) => {
    try {
      const warehouseData = await warehouseAPI.getById(warehouseId);
      setWarehouse(warehouseData);
    } catch (error) {
      toast.error(t('warehouses.loadError'));
      navigate('/warehouses');
    }
  };

  const loadInventory = async (warehouseId: number) => {
    try {
      setLoading(true);
      const result = await warehouseAPI.getFilteredInventory(warehouseId, {
        itemName: filters.itemName || undefined,
        unit: filters.unit || undefined,
        stockStatus: filters.stockStatus || undefined,
        page,
        pageSize,
      });
      setInventory(result.data);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch (error) {
      toast.error(t('warehouse.inventory.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page when filters change
  };

  const handleResetFilters = () => {
    setFilters({ itemName: undefined, unit: undefined, stockStatus: undefined });
    setPage(1);
    setPageSize(20);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when page size changes
  };

  const handleViewHistory = (itemId: number, itemName: string) => {
    setHistoryModal({
      isOpen: true,
      itemId,
      itemName,
    });
  };

  const handleReconcile = (itemId: number, itemName: string, systemStock: number, unit: string) => {
    setReconcileModal({
      isOpen: true,
      itemId,
      itemName,
      systemStock,
      unit,
    });
  };

  const handleReconcileSuccess = () => {
    if (id) {
      loadInventory(parseInt(id));
    }
  };

  const handleExportInventory = async () => {
    if (!warehouse) return;
    try {
      // If snapshot is active, export snapshot instead
      if (snapshotDate) {
        setExportingSnapshot(true);
        await warehouseAPI.downloadSnapshotExport(warehouse.id, snapshotDate);
        toast.success(t('warehouse.inventory.exportSnapshotSuccess'));
      } else {
        await warehouseAPI.downloadInventoryExport(warehouse.id, {
          itemName: filters.itemName || undefined,
          unit: filters.unit || undefined,
          stockStatus: filters.stockStatus || undefined,
        });
        toast.success(t('warehouse.inventory.exportSuccess'));
      }
    } catch (error) {
      toast.error(t('warehouse.inventory.exportError'));
    } finally {
      setExportingSnapshot(false);
    }
  };

  const handleViewSnapshot = async (date: string) => {
    if (!warehouse) return;
    try {
      setLoadingSnapshot(true);
      const snapshot = await warehouseAPI.getSnapshot(warehouse.id, date);
      setSnapshotData(snapshot);
      setSnapshotDate(date);
    } catch (error) {
      toast.error(t('warehouse.snapshot.loadError'));
    } finally {
      setLoadingSnapshot(false);
    }
  };

  const handleClearSnapshot = () => {
    setSnapshotDate(null);
    setSnapshotData(null);
  };

  const lowStockItems = inventory.filter((item) => item.currentStock > 0 && item.currentStock < 10);

  if (loading && !warehouse) {
    return <div className="text-center py-10">{t('common.loading')}</div>;
  }

  if (!warehouse) {
    return <div className="text-center py-10">{t('common.noData')}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/warehouses')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">{warehouse.name}</h1>
            <p className="text-gray-500 font-mono">{warehouse.code}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/warehouses/audit-logs?warehouseId=${warehouse.id}`)}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <ScrollText className="w-4 h-4" />
            Xem nhật ký
          </button>
          <button
            onClick={() => navigate(`/warehouses/${warehouse.id}/edit`)}
            className="btn-primary flex items-center"
          >
            <Edit className="w-4 h-4 mr-2" />
            {t('common.edit')}
          </button>
        </div>
      </div>

      {/* Warehouse Info */}
      <WarehouseInfoCard warehouse={warehouse} inventoryCount={total} />

      {/* Low Stock Alert */}
      {lowStockItems.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 text-yellow-800 mb-2">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-semibold">{t('warehouses.lowStockAlert')}</span>
          </div>
          <ul className="list-disc list-inside text-yellow-700 text-sm">
            {lowStockItems.slice(0, 5).map((item) => (
              <li key={item.id}>
                {item.item?.name || `Item #${item.itemId}`}: {item.currentStock} {item.unit.toLowerCase()}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Stock Snapshot Picker */}
      <SnapshotDatePicker
        onViewSnapshot={handleViewSnapshot}
        onClearSnapshot={handleClearSnapshot}
        activeSnapshot={snapshotDate}
        isLoading={loadingSnapshot}
      />

      {/* Show Snapshot or Normal Inventory */}
      {snapshotDate && snapshotData ? (
        <SnapshotResultTable
          items={snapshotData.items}
          snapshotDate={snapshotData.snapshotDate}
          warehouseName={snapshotData.warehouseName}
          warehouseCode={snapshotData.warehouseCode}
          onExport={handleExportInventory}
          isExporting={exportingSnapshot}
        />
      ) : (
        <>
          {/* Filters */}
          <InventoryFilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
          />

          {/* Inventory Table */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center">
            <Package className="w-5 h-5 mr-2" />
            {t('warehouses.inventory')}
          </h2>
          <button onClick={handleExportInventory} className="btn-secondary flex items-center">
            <Download className="w-4 h-4 mr-2" />
            Xuất Excel
          </button>
        </div>

        <InventoryTable
          inventory={inventory}
          loading={loading}
          onViewHistory={handleViewHistory}
          onReconcile={handleReconcile}
        />

        {totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            pageSize={pageSize}
            total={total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </div>
        </>
      )}

      {/* History Modal */}
      <ItemHistoryModal
        isOpen={historyModal.isOpen}
        onClose={() => setHistoryModal({ ...historyModal, isOpen: false })}
        warehouseId={warehouse.id}
        itemId={historyModal.itemId}
        itemName={historyModal.itemName}
        warehouseName={warehouse.name}
      />

      {/* Reconciliation Modal */}
      <ReconciliationModal
        isOpen={reconcileModal.isOpen}
        onClose={() => setReconcileModal({ ...reconcileModal, isOpen: false })}
        onSuccess={handleReconcileSuccess}
        warehouseId={warehouse.id}
        itemId={reconcileModal.itemId}
        itemName={reconcileModal.itemName}
        warehouseName={warehouse.name}
        systemStock={reconcileModal.systemStock}
        unit={reconcileModal.unit}
      />
    </div>
  );
};
